import { RICH_TEXT_CHARACTER_LIMIT } from '@cs-cms/types';

const validatorCache = new Map();

export const composeValidators =
  (...validators: ((value: any) => string | undefined)[]) =>
  (value: any): string | undefined => {
    for (const validator of validators) {
      const error = validator(value);
      if (error) {
        return error;
      }
    }
    return undefined;
  };

export const maxLengthRichTextEditor = (value: string, maxLengthMessage: string): string | undefined => {
  if (
    value !== undefined &&
    value !== '' &&
    value !== null &&
    value.replace(/<[^>]+>/g, '').length <= RICH_TEXT_CHARACTER_LIMIT
  ) {
    return undefined;
  }
  return maxLengthMessage;
};

export const requiredValidator = (value: string | number | boolean): string | undefined => {
  if (
    value !== undefined &&
    value !== '' &&
    value !== null &&
    value !== false &&
    (Array.isArray(value) ? value.length !== 0 : true)
  ) {
    return undefined;
  }
  return 'This field is required';
};

export const patternValidator =
  (regex: string) =>
  (value: string | number | boolean): string | undefined => {
    if (value === undefined || value === '' || value === null) {
      return undefined;
    }

    try {
      const regexPattern = new RegExp(regex);
      if (regexPattern.test(String(value))) {
        return undefined;
      }
      return 'Value does not match the required pattern';
    } catch {
      return 'Invalid regex pattern';
    }
  };

const isEmptyMinMaxValue = (value: string | number | null | undefined): boolean =>
  value === undefined || value === null || value === '';

const characterPlural = (count: number): string => (count !== 1 ? 's' : '');

const validateStringMinMax = (
  value: string,
  min: number | undefined,
  max: number | undefined
): string | undefined => {
  const length = value.length;

  if (min !== undefined && length < min) {
    return `This field must have at least ${min} character${characterPlural(min)}`;
  }

  if (max !== undefined && length > max) {
    return `This field must have at most ${max} character${characterPlural(max)}`;
  }

  return undefined;
};

const validateNumberMinMax = (
  value: number,
  min: number | undefined,
  max: number | undefined
): string | undefined => {
  if (min !== undefined && value < min) {
    return `This field must be greater than or equal to ${min}`;
  }

  if (max !== undefined && value > max) {
    return `This field must be less than or equal to ${max}`;
  }

  return undefined;
};

export const minMaxValidator =
  ({ min, max }: { min?: number; max?: number }) =>
  (value: string | number): string | undefined => {
    if (isEmptyMinMaxValue(value)) {
      return undefined;
    }

    if (typeof value === 'string') {
      return validateStringMinMax(value, min, max);
    }

    if (typeof value === 'number') {
      return validateNumberMinMax(value, min, max);
    }

    return undefined;
  };

export const buildFieldValidators = (field: { required?: boolean; regex?: string; min?: number; max?: number }) => {
  const cacheKey = JSON.stringify({
    required: field.required || false,
    regex: field.regex || '',
    min: field.min,
    max: field.max
  });

  if (validatorCache.has(cacheKey)) {
    return validatorCache.get(cacheKey);
  }

  const validators = [];

  if (field.required) {
    validators.push(requiredValidator);
  }

  if (field.regex) {
    validators.push(patternValidator(field.regex));
  }

  if (field.min !== undefined || field.max !== undefined) {
    validators.push(minMaxValidator(field));
  }

  const composedValidator = validators.length > 0 ? composeValidators(...validators) : undefined;

  validatorCache.set(cacheKey, composedValidator);

  return composedValidator;
};
